import type { CompanyStatusBadge } from '@/lib/company/status';
import type { CompanyOpenStatus } from '@/lib/company/working-hours';
import { resolveCompanyProfile } from '@/lib/image-placeholders';
import type { ApiCompany } from '@/types/api';
import { Heart, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';

interface CompanyCardProps {
  company: ApiCompany;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
  openStatus?: CompanyOpenStatus;
  statusBadge?: CompanyStatusBadge;
}

const CompanyCard = ({ company, isFavorite = false, onToggleFavorite, openStatus, statusBadge }: CompanyCardProps) => {
  const cityLabel = `${company?.neighborhood}, ${company?.city?.name} - ${company?.city?.state?.stateCode}`;
  const resolvedStatusBadge =
    statusBadge ??
    (openStatus
      ? {
          label: openStatus.isOpen ? 'Aberto agora' : 'Fechado',
          tone: openStatus.isOpen ? 'success' : 'muted',
        }
      : null);

  return (
    <div className="group border-border bg-card relative block overflow-hidden rounded-xl border transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg">
      {/* Favorite button */}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggleFavorite?.();
        }}
        className="bg-background/80 hover:bg-background absolute top-3 right-3 z-10 flex h-9 w-9 items-center justify-center rounded-full backdrop-blur-sm transition-colors"
        aria-label={isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
      >
        <Heart
          className={`h-4.5 w-4.5 transition-colors ${isFavorite ? 'fill-destructive text-destructive' : 'text-muted-foreground hover:text-destructive'}`}
        />
      </button>

      <Link to={`/${company.slug}`} className="block">
        <div className="bg-muted relative aspect-16/10 overflow-hidden">
          <img
            src={resolveCompanyProfile(company.profilePhoto)}
            alt={company.name}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        </div>

        <div className="p-4">
          <h3 className="text-card-foreground truncate text-base leading-tight font-semibold">{company.name}</h3>

          <div className="text-muted-foreground mt-2 flex items-center gap-1">
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate text-sm">{cityLabel}</span>
            {company.distanceKm != null && (
              <>
                <span className="mx-1 text-xs">·</span>
                <span className="text-primary shrink-0 text-sm font-medium">{company.distanceKm.toFixed(1)} km</span>
              </>
            )}
          </div>

          {resolvedStatusBadge && (
            <div className="mt-3">
              <span
                className={
                  resolvedStatusBadge.tone === 'success'
                    ? 'bg-success text-success-foreground inline-flex rounded-full px-2.5 py-1 text-xs font-medium'
                    : resolvedStatusBadge.tone === 'warning'
                      ? 'bg-warning text-warning-foreground inline-flex rounded-full px-2.5 py-1 text-xs font-medium'
                      : 'bg-muted text-muted-foreground inline-flex rounded-full px-2.5 py-1 text-xs font-medium'
                }
              >
                {resolvedStatusBadge.label}
              </span>
            </div>
          )}
        </div>
      </Link>
    </div>
  );
};

export default CompanyCard;
